import type { ComponentChildren } from "preact";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { ApiError } from "@tenergy/web-core/api";
import { Button, Field, Note, inputClass } from "../ui.js";

/**
 * EVERY MUTATION IN THIS PANEL GOES THROUGH HERE, and the four rules it enforces are the
 * reason it exists rather than each form doing its own thing.
 *
 *   1. **It shows what will change and when.** A publish form that only shows the new value
 *      makes the operator hold the old one in their head. The `changes` list is rendered above
 *      the button as `field: before → after`, and `effect` says when it starts applying.
 *   2. **The reason is mandatory**, because `audit_log.reason` is NOT NULL and the API refuses
 *      anything shorter than three characters. The button stays disabled until it is there, so
 *      the refusal is never the way an operator finds out.
 *   3. **API validation lands on the field it names.** `error.field` comes back on every
 *      `2001 validation_failed` (`apps/api/src/errors.ts`); `fieldErrors` below hands it to the
 *      input, and only an error with no field becomes a banner.
 *   4. **A destructive action cannot be submitted by Enter**, and takes a second press. Closing
 *      a contract and adjusting a ledger are not undone from the screen that did them.
 */

export interface ChangeLine {
  label: string;
  /** What it is now. `null` when there is nothing yet — shown as "not set". */
  before: string | null;
  after: string;
}

export interface MutationFormProps {
  title: string;
  /** One sentence: what this form does, in the operator's words. */
  description?: string;
  /** `field: before → after`, computed by the screen from the values it is about to send. */
  changes: ChangeLine[];
  /** When it starts applying, stated in full. Never "immediately" unless it really is. */
  effect: string;
  submitLabel: string;
  /** A second press, and no Enter. For anything that cannot be undone from this screen. */
  destructive?: boolean;
  busy?: boolean;
  /** Extra reason not to submit, e.g. a field the screen knows is empty. */
  disabled?: boolean;
  error: unknown;
  reason: string;
  onReason(value: string): void;
  onSubmit(): void;
  onCancel?: () => void;
  children: ComponentChildren;
}

/** The API's per-field message for `name`, or null. */
export function fieldError(error: unknown, name: string): string | null {
  if (!(error instanceof ApiError)) return null;
  return error.field === name ? error.message : null;
}

export function messageOf(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof Error) return error.message;
  return String(error);
}

/**
 * Whether the banner should carry this error, or a field already has it.
 *
 * ASKED OF THE DOM, not of a list the form has to keep in step. The error names a field; if an
 * input with that `name` is inside this form, that input is showing the message and repeating
 * it in a banner is noise. If the form does not render that field — the API blamed something
 * this screen does not offer — the banner is the only place the operator would ever see it, so
 * it goes there.
 */
function useBannerError(error: unknown, form: HTMLFormElement | null): string | null {
  const [banner, setBanner] = useState<string | null>(null);
  useEffect(() => {
    if (error === null || error === undefined) {
      setBanner(null);
      return;
    }
    const field = error instanceof ApiError ? error.field : null;
    if (field === null || form === null) {
      setBanner(messageOf(error));
      return;
    }
    const claimed = form.querySelector(`[name="${field.replace(/"/g, "")}"]`) !== null;
    setBanner(claimed ? null : messageOf(error));
  }, [error, form]);
  return banner;
}

export const MIN_REASON_LENGTH = 3;

export function MutationForm({
  title,
  description,
  changes,
  effect,
  submitLabel,
  destructive = false,
  busy = false,
  disabled = false,
  error,
  reason,
  onReason,
  onSubmit,
  onCancel,
  children,
}: MutationFormProps) {
  const [armed, setArmed] = useState(false);
  const [formNode, setFormNode] = useState<HTMLFormElement | null>(null);
  const banner = useBannerError(error, formNode);
  const reasonOk = reason.trim().length >= MIN_REASON_LENGTH;
  const canSubmit = reasonOk && !busy && !disabled;

  const submit = useCallback(() => {
    if (!canSubmit) return;
    if (destructive && !armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    onSubmit();
  }, [canSubmit, destructive, armed, onSubmit]);

  return (
    <form
      ref={setFormNode}
      class="space-y-3"
      aria-label={title}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      onKeyDown={(event: KeyboardEvent) => {
        // A destructive form is never submitted by a stray Return in a text field. The key is
        // swallowed rather than passed on, so nothing else can pick it up either; a textarea
        // still gets its newline.
        if (!destructive || event.key !== "Enter") return;
        const target = event.target as HTMLElement | null;
        if (target?.tagName === "TEXTAREA") return;
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <div>
        <h3 class="text-[13px] font-semibold text-ink">{title}</h3>
        {description === undefined ? null : (
          <p class="mt-0.5 text-[11px] leading-relaxed text-ink-muted">{description}</p>
        )}
      </div>

      {/* Capped, and two columns at most. A field box eleven hundred pixels wide holding a
          four-digit price is harder to read than a short one, not easier. */}
      <div class="grid max-w-3xl gap-3 sm:grid-cols-2">{children}</div>

      <div class="max-w-3xl">
      <Field
        label="Reason (required, written to the audit log)"
        hint={`At least ${MIN_REASON_LENGTH} characters. Everybody who reads this change later reads this sentence.`}
        error={fieldError(error, "reason")}
      >
        <input
          class={inputClass}
          name="reason"
          value={reason}
          required
          minLength={MIN_REASON_LENGTH}
          placeholder="Why this change, in one line"
          onInput={(event) => onReason((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      </div>

      <div class="max-w-3xl rounded-[12px] border border-line bg-surface-raised p-2.5">
        <div class="text-[11px] font-medium text-ink">What this will change</div>
        {changes.length === 0 ? (
          <p class="mt-1 text-[11px] text-ink-muted">
            Nothing yet — fill the fields above and the change will be listed here.
          </p>
        ) : (
          <ul class="mt-1 space-y-0.5">
            {changes.map((line) => (
              <li key={line.label} class="text-[11px] tabular-nums text-ink-muted">
                <span class="text-ink">{line.label}</span>:{" "}
                <span>{line.before ?? "not set"}</span> <span aria-hidden="true">→</span>{" "}
                <span class="text-ink">{line.after}</span>
              </li>
            ))}
          </ul>
        )}
        <div class="mt-1.5 border-t border-line pt-1.5 text-[11px] text-ink-muted">
          <span class="text-ink">Takes effect</span>: {effect}
        </div>
      </div>

      {banner === null ? null : <Note tone="danger">{banner}</Note>}

      {destructive && armed ? (
        <Note tone="warning">
          This cannot be undone from this screen. Press “{submitLabel}” again to go ahead.
        </Note>
      ) : null}

      <div class="flex items-center gap-2">
        <Button
          type="submit"
          tone={destructive ? "danger" : "primary"}
          disabled={!canSubmit}
          title={reasonOk ? undefined : "A reason is required"}
        >
          {busy ? "Working…" : armed ? `${submitLabel} — confirm` : submitLabel}
        </Button>
        {onCancel === undefined ? null : (
          <Button
            tone="ghost"
            onClick={() => {
              setArmed(false);
              onCancel();
            }}
          >
            Cancel
          </Button>
        )}
        {reasonOk ? null : (
          <span class="text-[11px] text-ink-muted">A reason is required before publishing.</span>
        )}
      </div>
    </form>
  );
}
